import React from "react";

function About() {
    return (
        <div className="pt-24 pb-16 text-white min-h-screen">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                        About Meerkado
                    </h1>
                    <p className="text-blue-200 text-lg">Established 2023 • Mobile Game Studio</p>
                </div>

                {/* Introduction */}
                <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-2xl p-6 md:p-8 mb-8 shadow-lg border border-blue-500/20">
                    <p className="text-blue-100 leading-relaxed text-lg mb-4">
                        Founded in <span className="font-semibold text-white">2023</span>, Meerkado Game Studio set out with a clear mission:
                        to create mobile gaming experiences that go beyond pure entertainment — experiences that engage the player's emotions,
                        imagination, and intellect, bridging technology, art, and human storytelling.
                    </p>
                    <p className="text-blue-100 leading-relaxed text-lg">
                        At Meerkado, we see games not merely as products, but as a <span className="font-semibold text-white">powerful medium of expression</span> —
                        one that can inspire, connect, and open new worlds to players. That's why our focus has always been on high-quality design,
                        innovation in gameplay and narrative, and a deep understanding of our audience.
                    </p>
                </div>

                {/* Story Section */}
                <section className="mb-8 bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-6 md:p-8 shadow-lg border border-white/5">
                    <h2 className="text-2xl font-bold mb-4 text-blue-300">Our Journey</h2>
                    <p className="text-blue-100 leading-relaxed mb-4">
                        Since its founding, Meerkado has built a <span className="font-semibold text-white">strong reputation by developing 
                        and publishing</span> successful titles enjoyed by millions of players across different countries. Our passionate and skilled team 
                        continuously strives to create experiences that resonate with diverse audiences, keeping pace with the latest global trends 
                        in the gaming industry.
                    </p>
                    <p className="text-blue-100 leading-relaxed">
                        Collaboration with fresh talent and creative teams is one of our core values. We believe the future of game development
                        will be shaped through cooperation, education, and empowering the next generation. For this reason, Meerkado has always acted
                        as a <span className="font-semibold text-white">bridge between developers, artists, and publishers</span> — building a sustainable
                        foundation for the growth of the gaming ecosystem.
                    </p>
                </section>

                {/* Values Section */}
                <section className="mb-8">
                    <h2 className="text-3xl font-bold mb-6 text-center text-blue-300">Our Values & Beliefs</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Value 1 */}
                        <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-6 shadow-lg border border-white/5 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🎮</div>
                                <h3 className="text-xl font-bold text-blue-300">Creativity & Innovation</h3>
                            </div>
                            <p className="text-blue-100 leading-relaxed">
                                We seek fresh ideas that push the boundaries of gameplay and continuously surprise and delight players.
                            </p>
                        </div>

                        {/* Value 2 */}
                        <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-6 shadow-lg border border-white/5 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🌍</div>
                                <h3 className="text-xl font-bold text-blue-300">Quality & Authenticity</h3>
                            </div>
                            <p className="text-blue-100 leading-relaxed">
                                We believe every detail — from visual design to sound — shapes the overall experience and deserves to be crafted with excellence.
                            </p>
                        </div>

                        {/* Value 3 */}
                        <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-6 shadow-lg border border-white/5 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🤝</div>
                                <h3 className="text-xl font-bold text-blue-300">Collaboration & Learning</h3>
                            </div>
                            <p className="text-blue-100 leading-relaxed">
                                We foster a dynamic, growth-oriented environment where individuals can learn, evolve, and achieve greater goals together.
                            </p>
                        </div>

                        {/* Value 4 */}
                        <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-6 shadow-lg border border-white/5 hover:border-blue-500/30 transition">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="text-3xl">🚀</div>
                                <h3 className="text-xl font-bold text-blue-300">Sustainability & Growth</h3>
                            </div>
                            <p className="text-blue-100 leading-relaxed">
                                Meerkado is future-focused — building a sustainable infrastructure for game development and a strong presence in the global market.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Vision Section */}
                <section className="mb-8 bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-xl p-6 md:p-8 shadow-lg border border-blue-500/30">
                    <h2 className="text-3xl font-bold mb-6 text-center text-blue-300">Our Vision</h2>
                    <div className="space-y-4 text-blue-100">
                        <p className="text-lg leading-relaxed">
                            Our vision is to become the <span className="font-semibold text-white">leading platform for mobile game development and publishing
                                in the Middle East</span>, empowering local and regional talents to create games that shine on the global stage.
                        </p>
                        <p className="text-lg leading-relaxed">
                            We aim to play an active role in shaping the future of the digital entertainment industry — a future where games are not only
                            a form of fun, but a <span className="font-semibold text-white">platform for learning, connection, identity, and social transformation</span>.
                        </p>
                        <p className="text-lg leading-relaxed font-semibold text-center mt-6">
                            At Meerkado, we move toward that future step by step — with conviction, creativity, and boundless passion.
                        </p>
                    </div>
                </section>

                {/* Mission Statement */}
                <div className="mt-12 bg-gradient-to-br from-[#021428] to-[#011226] rounded-2xl p-8 shadow-lg border border-blue-500/20 text-center">
                    <h2 className="text-2xl font-bold mb-4 text-blue-300">Our Promise</h2>
                    <p className="text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto italic">
                        "We believe in a future where games will play a more influential role than ever — in education, culture,
                        social interaction, and even major decision-making. And Meerkado stands at the heart of that journey —
                        <span className="font-semibold text-white"> where dreams turn into games</span>."
                    </p>
                </div>

                {/* Stats/Highlights */}
                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-4 text-center border border-white/5">
                        <div className="text-3xl font-bold text-blue-400 mb-1">+2</div>
                        <div className="text-sm text-blue-200">Years of Excellence</div>
                    </div>
                    <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-4 text-center border border-white/5">
                        <div className="text-3xl font-bold text-blue-400 mb-1">Millions</div>
                        <div className="text-sm text-blue-200">Players Worldwide</div>
                    </div>
                    <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-4 text-center border border-white/5">
                        <div className="text-3xl font-bold text-blue-400 mb-1">Global</div>
                        <div className="text-sm text-blue-200">Market Presence</div>
                    </div>
                    <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-xl p-4 text-center border border-white/5">
                        <div className="text-3xl font-bold text-blue-400 mb-1">Innovation</div>
                        <div className="text-sm text-blue-200">Driven Studio</div>
                    </div>
                </div>

                {/* CTA */}
                <div className="mt-12 text-center">
                    <p className="text-blue-200 mb-4">Want to join us on this journey?</p>
                    <a
                        href="/contact"
                        className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition text-white font-semibold"
                    >
                        Get in Touch
                    </a>
                </div>
            </div>
        </div>
    );
}

export default About;